import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { BUSINESS, NAV_LINKS } from "@/data/site";
import { EASE } from "@/components/Reveal";

export function DiamondMark({ className = "h-6 w-6" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
      <path d="M12 2 L21 9 L12 22 L3 9 Z" stroke="currentColor" strokeWidth="1.4" />
      <path d="M3 9 H21 M12 2 L8 9 L12 22 L16 9 L12 2" stroke="currentColor" strokeWidth="0.9" opacity="0.7" />
    </svg>
  );
}

export default function Nav() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const linkClass = ({ isActive }) =>
    `text-[12px] uppercase tracking-[0.14em] transition-colors duration-200 ${
      isActive ? "text-[#9A7A3F]" : "text-[#3D352E] hover:text-[#9A7A3F]"
    }`;

  return (
    <>
      <header className="sticky top-0 z-50 border-b border-[#D4AF37]/20 bg-[#FAF7F2]/85 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 md:h-20 lg:px-8">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-brand-link">
            <img
              src="/assets/logo.png"
              alt="Diamond's Divine Arts logo"
              className="h-10 w-10 rounded-full object-cover ring-1 ring-[#D4AF37]/50 md:h-11 md:w-11"
            />
            <span className="leading-tight">
              <span className="block font-display text-[12px] font-semibold tracking-[0.2em] text-[#1C1815] md:text-[13px]">
                DIAMOND'S DIVINE ARTS
              </span>
              <span className="mt-0.5 block font-mono text-[8.5px] uppercase tracking-[0.3em] text-[#8C7E70]">
                Faith · Art · Divine Inspiration
              </span>
            </span>
          </Link>

          <nav className="hidden items-center gap-6 xl:flex" aria-label="Primary">
            {NAV_LINKS.map((l) => (
              <NavLink key={l.to} to={l.to} className={linkClass} data-testid={`nav-link-${l.label.toLowerCase()}`}>
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <Link
              to="/booking"
              className="btn-primary hidden !px-6 !py-2.5 !text-xs md:inline-flex"
              data-testid="nav-book-button"
            >
              Book a Consultation
            </Link>
            <button
              type="button"
              className="p-2 xl:hidden"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
              data-testid="nav-menu-button"
            >
              <Menu className="h-6 w-6 text-[#1C1815]" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[60] flex flex-col bg-[#171310] text-[#FAF7F2]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            data-testid="nav-mobile-menu"
          >
            <div className="flex h-16 items-center justify-between border-b border-[#D4AF37]/20 px-4 sm:px-6">
              <span className="flex items-center gap-3">
                <img
                  src="/assets/logo.png"
                  alt="Diamond's Divine Arts logo"
                  className="h-9 w-9 rounded-full object-cover ring-1 ring-[#D4AF37]/50"
                />
                <span className="font-display text-[12px] font-semibold tracking-[0.2em]">
                  DIAMOND'S DIVINE ARTS
                </span>
              </span>
              <button
                type="button"
                className="p-2"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                data-testid="nav-close-button"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <nav className="flex flex-1 flex-col justify-center px-8" aria-label="Mobile">
              {[
                ...NAV_LINKS.filter((l) => l.label !== "Contact"),
                { to: "/booking", label: "Book" },
                { to: "/contact", label: "Contact" },
              ].map(
                (l, i) => (
                  <motion.div
                    key={l.to}
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.06 + i * 0.06, duration: 0.5, ease: EASE }}
                  >
                    <NavLink
                      to={l.to}
                      className="block border-b border-[#FAF7F2]/10 py-3 font-serif text-3xl font-light tracking-tight transition-colors hover:text-[#D4AF37]"
                      data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                    >
                      {l.label}
                    </NavLink>
                  </motion.div>
                )
              )}
            </nav>

            <div className="flex flex-col gap-2 px-8 pb-10 text-sm text-[#B7AA99]">
              <a href={`mailto:${BUSINESS.email}`} className="transition-colors hover:text-[#D4AF37]" data-testid="nav-mobile-email">
                {BUSINESS.email}
              </a>
              <a href={BUSINESS.phoneHref} className="transition-colors hover:text-[#D4AF37]" data-testid="nav-mobile-phone">
                {BUSINESS.phone}
              </a>
              <a
                href={BUSINESS.instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="transition-colors hover:text-[#D4AF37]"
                data-testid="nav-mobile-instagram"
              >
                {BUSINESS.instagram}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
